import React, { Component } from 'react'
import { NavLink } from 'react-router-dom'
import Icon from '../Icons'
import AppContext from 'AppContext'
import { hasAccess, featureEnabled, adminEmail } from 'components/Helpers'

const baseUrl = (operatorID) => (
  '/operators/' + operatorID
)

const DASHBOARD_VIEW = [
  'reports.view_memberships',
  'reports.view_financial',
  'reports.view_access',
  'reports.view_pts'
]

const REPORTS_VIEW = [
  'reports.view_memberships',
  'reports.view_financial',
  'reports.view_pts'
]

const ManagementLinks = (operatorID, env) => {
  const links = [
    {
      title: 'Dashboard',
      path: '/dashboard',
      icon: 'house',
      permission: DASHBOARD_VIEW
    },
    {
      title: 'Locations',
      path: baseUrl(operatorID) + '/locations',
      icon: 'location',
      permission: 'operators.view_locations'
    },
    {
      title: 'Memberships',
      path: baseUrl(operatorID) + '/memberships',
      icon: 'customers',
      permission: 'memberships.view_memberships'
    },
    {
      title: 'Free passes',
      path: baseUrl(operatorID) + '/free-passes/active/list',
      icon: 'freePasses',
      permission: 'free_passes.view_passes'
    },
    {
      title: 'Paid passes',
      path: baseUrl(operatorID) + '/paid-passes-v2/distributors',
      icon: 'freePasses',
      permission: 'paid_passes.view_passes'
    },
    {
      title: 'PTs',
      path: baseUrl(operatorID) + '/pts/by-location', // not making Vaadin redirect from / work, we're falling back on linking directly to 1st view
      icon: 'pts',
      permission: 'pts.view_pts'
    },
    {
      title: 'Reports',
      path: baseUrl(operatorID) + '/reports',
      icon: 'reports',
      permission: REPORTS_VIEW
    },
    {
      title: 'Product',
      path: baseUrl(operatorID) + '/product/details',
      icon: 'product',
      activePaths: ['product'],
      permission: 'memberships.view_product'
    },
    {
      title: 'Add-ons',
      path: baseUrl(operatorID) + '/add-ons/pt-packages',
      icon: 'product',
      activePaths: ['add-ons'],
      permission: 'memberships.view_addons'
    },
    {
      title: 'Promotions',
      path: baseUrl(operatorID) + '/promotions-v2/sales/list',
      icon: 'promotion',
      permission: 'memberships.view_promotions'
    },
    {
      title: 'Maintenance',
      path: baseUrl(operatorID) + '/maintenance/issues/list',
      icon: 'maintenance',
      permission: 'facility_management.view_issues'
    }
  ]

  // only show in CH and DE
  if (featureEnabled('leads', operatorID)) {
    const leads = {
      title: 'Leads',
      path: baseUrl(operatorID) + '/leads/list',
      icon: 'customers',
      permission: 'leads.view_leads'
    }
    links.splice(11, 0, leads)
  }

  // Only show wellpass in DE
  if (featureEnabled('wellpass', operatorID)) {
    links.splice(12, 0, {
      title: 'Wellpass',
      path: baseUrl(operatorID) + '/egym-wellpass',
      icon: 'customers',
      permission: 'memberships.view_memberships'
    })
  }

  // Only show dccuments in AT
  if (featureEnabled('documents', operatorID)) {
    links.splice(11, 0, {
      title: 'Documents',
      path: baseUrl(operatorID) + '/documents/list',
      icon: 'document',
      permission: 'memberships.view_documents'
    })
  }

  if (featureEnabled('templates', operatorID)) {
    links.splice(11, 0, {
      title: 'SMS Templates',
      path: baseUrl(operatorID) + '/sms-templates/list',
      icon: 'sms',
      permission: 'memberships.view_memberships'
    })
  }

  return links
}

const SystemLinks = (operatorID) => (
  [
    {
      title: 'Administrators',
      path: baseUrl(operatorID) + '/administrators',
      icon: 'lock',
      permission: 'administrators.view_administrators'
    },
    {
      title: 'Key Management',
      path: baseUrl(operatorID) + '/key-management',
      icon: 'maintenance',
      permission: 'access_control.view_keys'
    },
    {
      title: 'Monitoring',
      path: baseUrl(operatorID) + '/monitor',
      icon: 'maintenance',
      permission: 'access_control.view_monitor'
    },
    {
      title: 'Settings',
      path: baseUrl(operatorID) + '/settings',
      icon: 'settings',
      betaUsers: ['teknisk@fitnessgroup.no']
    }
  ]
)

const navGroups = (operatorID, env) => (
  [
    {
      header: 'MANAGEMENT',
      links: ManagementLinks(operatorID, env)
    },
    {
      header: 'SYSTEM',
      links: SystemLinks(operatorID)
    }
  ]
)

const LinkItem = ({ link, method }) => {
  // If a link has an array of activePaths
  // then we override the isActive and checks
  // if we find the routes included
  const checkActive = (match, location) => {
    const locArray = location.pathname.split('/')
    const matchingPath = locArray.filter(
      p => link.activePaths.includes(p)
    )

    return matchingPath.length > 0
  }

  return (
    <NavLink
      to={link.path}
      onClick={() => method(link.name)}
      activeClassName='active'
      isActive={link.activePaths ? checkActive : null}
    >
      <li>
        <Icon type={link.icon} />
        {link.title}
      </li>
    </NavLink>
  )
}

class SideBar extends Component {
  render () {
    const mobileNavClass = this.props.mobile_nav_hidden ? 'hidden' : 'visible'
    return (
      <div id='sidebar' className={mobileNavClass}>
        <ul>
          {this._linkGroups(this.props.env)}
          {/* Logout link */}
          <li onClick={this.props.logout} style={{ cursor: 'pointer' }}>
            <Icon type='trash' />
            Logout
          </li>
        </ul>
      </div>
    )
  }

  // NOTE: setting `betaUsers: [email_list]` in XxxLinks will allow selective access to the sidebar item
  _hasBetaAccess (link) {
    if (!link.betaUsers || link.betaUsers.length === 0) {
      return true
    }
    const user = JSON.parse(window.localStorage.getItem('currentAdmin'))
    if (!user || (user && link.betaUsers.includes(adminEmail(user)))) {
      return true
    }
    return false
  }

  _hasAccess (link) {
    if (!this._hasBetaAccess(link)) return false
    if (!link.permission) return true
    return hasAccess({ permissions: this.props.permissions }, link.permission)
  }

  _linkGroups = (env) => {
    const context = this.context // seem to be required lo load context _before_ retrieving operator ID
    return (
      navGroups(context.operator.id, env).map((group) => {
        const visibleLinks = group.links.filter(link => this._hasAccess(link))
        if (visibleLinks.length === 0) return null
        return (
          [
            <li key={group.header} className='header'>
              {group.header}
            </li>,
            visibleLinks.map((link) =>
              <LinkItem
                key={link.title}
                link={link}
                method={this.props.toggle_mobile_nav}
              />
            )
          ]
        )
      })
    )
  }
}

SideBar.contextType = AppContext

export default SideBar
export { navGroups }
